import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Building, 
  User, 
  Clock,
  MapPin,
  CheckCircle
} from 'lucide-react';
import Avatar from '../components/common/Avatar';
import Button from '../components/common/Button';
import Select from '../components/common/Select';
import { useAppContext } from '../contexts/AppContext';
import { Guest } from '../types';

const TurnstileDisplay: React.FC = () => {
  const { guests, checkInGuest, checkOutGuest } = useAppContext();
  const navigate = useNavigate();
  const [selectedGuestId, setSelectedGuestId] = useState<string>('');
  const [currentGuest, setCurrentGuest] = useState<Guest | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const [showSelector, setShowSelector] = useState(false);

  // Get guests with cards who can check-in (haven't checked in yet)
  const checkInGuests = guests.filter(guest => 
    guest.status === 'approved' && guest.cardId && !guest.checkInTime
  );

  // Get guests with cards who can check-out (checked in but not out)
  const checkOutGuests = guests.filter(guest => 
    guest.status === 'approved' && guest.cardId && guest.checkInTime && !guest.checkOutTime
  );

  // Combine both lists with action indicator
  const availableGuests = [
    ...checkInGuests.map(guest => ({ ...guest, action: 'check-in' as const })),
    ...checkOutGuests.map(guest => ({ ...guest, action: 'check-out' as const }))
  ];

  // Create options for select dropdown
  const guestOptions = availableGuests.map(guest => ({
    value: guest.id,
    label: `${guest.fullName} - ${guest.company} (Tầng ${guest.floor}) - ${guest.action === 'check-in' ? 'VÀO' : 'RA'}`
  }));

  // Show selector after 2 seconds
  useEffect(() => {
    console.log('TurnstileDisplay - Available guests:', availableGuests.length, availableGuests);
    const timer = setTimeout(() => {
      console.log('TurnstileDisplay - After 2s, checking:', availableGuests.length, !currentGuest);
      if (availableGuests.length > 0 && !currentGuest) {
        console.log('TurnstileDisplay - Showing selector');
        setShowSelector(true);
      } else {
        console.log('TurnstileDisplay - Not showing selector, no available guests or currentGuest exists');
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [availableGuests, currentGuest]);

  const handleGuestSelect = (guestId: string) => {
    setSelectedGuestId(guestId);
  };

  const handleAction = () => {
    if (selectedGuestId) {
      const guest = availableGuests.find(g => g.id === selectedGuestId);
      if (guest) {
        setCurrentGuest(guest);
        setAnimationClass('animate-fade-in');
        setIsVisible(true);
        setShowSelector(false);
        
        // Perform check-in or check-out based on guest action
        if (guest.action === 'check-in') {
          checkInGuest(guest.id);
        } else {
          checkOutGuest(guest.id);
        }
        
        // Auto navigate to statistics after 3 seconds
        setTimeout(() => {
          navigate('/statistics');
        }, 3000);
      }
    }
  };

  const handleReset = () => {
    setCurrentGuest(null);
    setSelectedGuestId('');
    setIsVisible(false);
    setShowSelector(true);
  };

  const getElevatorZoneColor = (zone: string) => {
    switch (zone) {
      case 'A': return 'bg-blue-500 text-white';
      case 'B': return 'bg-green-500 text-white';
      case 'C': return 'bg-purple-500 text-white';
      default: return 'bg-secondary-500 text-white';
    }
  };

  const getElevatorZoneIcon = (zone: string) => {
    return (
      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${getElevatorZoneColor(zone)}`}>
        {zone}
      </div>
    );
  };

  // Show guest selector (combobox + button)
  if (showSelector && !currentGuest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full">
          <div className="text-center text-white mb-8">
            <h1 className="text-4xl font-bold mb-4">
              Hệ Thống Quản Lý Ra Vào
            </h1>
            <p className="text-xl text-white/80">
              Chọn khách để demo quét thẻ vào tòa nhà
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <h2 className="text-2xl font-bold text-secondary-900 mb-8 text-center">
              Demo Check-in
            </h2>
            
            {availableGuests.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-secondary-500 mb-4 text-lg">
                  Không có khách nào sẵn sàng để check-in
                </div>
                <p className="text-secondary-400">
                  Vui lòng đăng ký và cấp thẻ cho khách trước
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <Select
                  label="Chọn khách vãng lai"
                  placeholder="-- Chọn khách để check-in --"
                  options={guestOptions}
                  value={selectedGuestId}
                  onChange={(e) => handleGuestSelect(e.target.value)}
                />
                
                <div className="text-center pt-4">
                  <Button
                    size="xl"
                    onClick={handleAction}
                    disabled={!selectedGuestId}
                    className="px-16 py-4 text-2xl font-bold"
                  >
                    {selectedGuestId ? 
                      (availableGuests.find(g => g.id === selectedGuestId)?.action === 'check-in' ? 'VÀO' : 'RA')
                      : 'VÀO'
                    }
                  </Button>
                </div>
                
                <div className="text-center text-sm text-secondary-500">
                  Chọn khách từ dropdown và nhấn "VÀO" hoặc "RA" để demo
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show waiting state if no guest selected
  if (!isVisible || !currentGuest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="mb-8">
            <div className="mx-auto w-32 h-32 border-4 border-white/30 rounded-full flex items-center justify-center mb-6">
              <div className="w-20 h-20 border-4 border-white rounded-full flex items-center justify-center">
                <div className="w-12 h-12 bg-white rounded-full animate-pulse-slow"></div>
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">
              Hệ Thống Quản Lý Ra Vào
            </h1>
            <p className="text-xl text-white/80">
              Check-in khách...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-success-500 to-success-700 flex items-center justify-center p-8">
      <div className={`max-w-4xl w-full ${animationClass}`}>
        {/* Welcome Header */}
        <div className="text-center text-white mb-12">
          <div className="flex items-center justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-white mr-4" />
            <h1 className="text-5xl font-bold">Chào mừng!</h1>
          </div>
          <p className="text-2xl text-white/90">
            Thông tin truy cập của bạn
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Guest Info */}
            <div className="text-center lg:text-left">
              <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-6">
                <Avatar 
                  src={currentGuest.photo} 
                  size="2xl" 
                  className="ring-4 ring-success-200"
                />
                <div className="flex-1">
                  <h2 className="text-4xl font-bold text-secondary-900 mb-4">
                    {currentGuest.fullName}
                  </h2>
                  <div className="space-y-3 text-lg text-secondary-600">
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <Building className="w-6 h-6 text-primary-500" />
                      <span className="font-medium">{currentGuest.company}</span>
                    </div>
                    {currentGuest.contactPerson && (
                      <div className="flex items-center justify-center lg:justify-start space-x-3">
                        <User className="w-6 h-6 text-primary-500" />
                        <span>Gặp: {currentGuest.contactPerson}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <Clock className="w-6 h-6 text-primary-500" />
                      <span>{new Date().toLocaleTimeString('vi-VN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <ArrowRight className="w-6 h-6 text-success-500" />
                      <span className="font-bold text-success-600">
                        {availableGuests.find(g => g.id === currentGuest.id)?.action === 'check-in' ? 'VÀO TÒA NHÀ' : 'RA KHỎI TÒA NHÀ'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Floor & Elevator Info */}
            <div className="text-center">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-secondary-900 mb-6">
                  Thông tin di chuyển
                </h3>
                
                {/* Floor Number */}
                <div className="mb-8">
                  <div className="text-6xl font-bold text-primary-600 mb-2">
                    {currentGuest.floor}
                  </div>
                  <div className="text-xl text-secondary-600">
                    Tầng cần đi
                  </div>
                </div>

                {/* Elevator Zone */}
                <div className="mb-8">
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    {getElevatorZoneIcon(currentGuest.elevatorZone || 'A')}
                    <div className="text-left">
                      <div className="text-2xl font-bold text-secondary-900">
                        Khu {currentGuest.elevatorZone || 'A'}
                      </div>
                      <div className="text-secondary-600">
                        Khu vực thang máy
                      </div>
                    </div>
                  </div>
                </div>

                {/* Direction */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-center space-x-3 text-lg font-semibold text-secondary-900">
                    <MapPin className="w-6 h-6 text-success-500" />
                    <span>Vui lòng di chuyển đến</span>
                  </div>
                  <div className="mt-2 text-2xl font-bold text-success-600">
                    Khu thang máy {currentGuest.elevatorZone || 'A'}
                  </div>
                  <div className="flex items-center justify-center mt-4">
                    <ArrowRight className="w-8 h-8 text-success-500 animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-2 bg-success-100 text-success-800 px-8 py-4 rounded-full text-xl font-medium">
              <CheckCircle className="w-8 h-8" />
              <span>Check-in thành công!</span>
            </div>
            <div className="mt-4 text-white/80 text-sm">
              Màn hình sẽ tự động quay lại sau vài giây...
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurnstileDisplay;
